import { TimeAgo } from '@/ui'
import type { WhatsAppConversationData } from '@jobilla/nexus-sdk'
import { format, isSameDay, isValid, parseISO } from 'date-fns'
import { ClockIcon, FileTextIcon, MessageCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { replyWindowState } from './reply-window'

export type ConversationRowProps = {
	conversation: WhatsAppConversationData
	isSelected: boolean
	onSelect: (conversation: WhatsAppConversationData) => void
}

export function ConversationRow({ conversation, isSelected, onSelect }: ConversationRowProps) {
	const { t } = useTranslation('common')
	const isSelectable = Boolean(conversation.campaignId)
	const candidateName = conversation.candidateName ?? t('whatsappInbox.anonymisedCandidate')
	// TimeAgo throws on an invalid date — omit the timestamp instead of crashing the row.
	const parsedLastMessageAt = conversation.lastMessageAt ? parseISO(conversation.lastMessageAt) : null
	const lastMessageDate = parsedLastMessageAt && isValid(parsedLastMessageAt) ? parsedLastMessageAt : null
	// On needs-reply rows the last message is the inbound one, so the free-form
	// window is derivable from it. Informational, deliberately not a countdown.
	const replyWindow = conversation.needsReply
		? replyWindowState(conversation.lastMessageAt, new Date())
		: null

	const handleClick = () => {
		if (conversation.campaignId) {
			onSelect(conversation)
		}
	}

	return (
		<button
			type="button"
			disabled={!isSelectable}
			onClick={handleClick}
			aria-current={isSelected}
			className={`flex w-full items-start gap-3 border-b border-grey-100 px-4 py-3 text-left transition-colors ${
				isSelectable ? 'cursor-pointer hover:bg-grey-50' : 'cursor-not-allowed opacity-60'
			} ${isSelected ? 'bg-purple-50/50' : ''}`}
		>
			<div className="mt-0.5 flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
				<MessageCircleIcon className="size-4 text-green-600" />
			</div>

			<div className="min-w-0 flex-1">
				<div className="flex items-center justify-between gap-2">
					<span className="truncate font-medium text-grey-900">{candidateName}</span>
					{lastMessageDate
						? (
							<span className="flex-shrink-0 text-xs text-grey-400">
								<TimeAgo date={lastMessageDate} />
							</span>
						)
						: null}
				</div>

				{conversation.campaignTitle
					? <p className="truncate text-xs text-grey-500">{conversation.campaignTitle}</p>
					: null}

				{conversation.lastMessagePreview
					? <p className="mt-0.5 truncate text-sm text-grey-600">{conversation.lastMessagePreview}</p>
					: null}

				<div className="mt-1.5 flex items-center gap-2">
					{conversation.needsReply
						? (
							<span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
								{t('whatsappInbox.needsReply')}
							</span>
						)
						: null}
					{conversation.optedOut
						? (
							<span className="inline-flex items-center rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium text-grey-600">
								{t('whatsappInbox.optedOut')}
							</span>
						)
						: null}
					{replyWindow
						? (
							<span
								title={replyWindow.expiresAt.toLocaleString()}
								className="ml-auto inline-flex items-center gap-1 text-xs text-grey-400 tabular-nums"
							>
								{replyWindow.isOpen
									? <ClockIcon className="size-3" aria-hidden="true" />
									: <FileTextIcon className="size-3" aria-hidden="true" />}
								{replyWindow.isOpen
									? t(
										isSameDay(replyWindow.expiresAt, new Date())
											? 'whatsappInbox.replyWindowUntil'
											: 'whatsappInbox.replyWindowUntilTomorrow',
										{ time: format(replyWindow.expiresAt, 'HH:mm') },
									)
									: t('whatsappInbox.templateNeeded')}
							</span>
						)
						: null}
				</div>
			</div>

			{conversation.unreadCount > 0 && (
				<span
					aria-label={t('whatsappInbox.unread')}
					className="flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-purple-500 px-1.5 text-xs font-semibold text-white"
				>
					{conversation.unreadCount}
				</span>
			)}
		</button>
	)
}
